/**
 * OAuth Authentication Service
 * Handles login, logout, and token management for web app authentication
 * Supports multiple OAuth providers: Discord and XIVAuth
 *
 * Uses PKCE (Proof Key for Code Exchange) for secure OAuth flow in SPAs
 *
 * @module services/auth-service
 */

import { logger } from '@shared/logger';
// FINDING-008: Import APIService to clear cache on logout
import { APIService } from './api-service-wrapper.js';
import type { AuthProvider, AuthUser, AuthResponse, JWTPayload } from '@xivdyetools/types';

// ============================================
// Types
// ============================================

// AuthProvider, AuthUser, AuthResponse and JWTPayload are the shared
// `@xivdyetools/types` contracts (AuthUser/AuthProvider re-exported below —
// AuthState's fields keep them part of this module's public shape).
// `primary_character` (a nested field on AuthUser/JWTPayload) is never read
// in this file: oauth 3.0.0 stopped minting it, and this file's own readers
// of it were removed with it (FINDING-002 carried item). The optional field
// stays on the shared type until Sprint 11, so PrimaryCharacter itself is
// left unimported — nothing here names it.
export type { AuthProvider, AuthUser };

export interface AuthState {
  isAuthenticated: boolean;
  user: AuthUser | null;
  token: string | null;
  expiresAt: number | null;
  provider: AuthProvider | null;
}

export type AuthStateListener = (state: AuthState) => void;

/**
 * Identity the presets API keys ownership by — see `AuthUser.id`.
 * Mirrors `resolveJWTUserId()` in apps/presets-api/src/middleware/auth.ts.
 */
function presetsIdentity(payload: JWTPayload): string {
  return typeof payload.discord_id === 'string' && payload.discord_id.length > 0
    ? payload.discord_id
    : payload.sub;
}

// ============================================
// Configuration
// ============================================

/**
 * OAuth Worker URL - handles Discord OAuth flow
 */
const OAUTH_WORKER_URL = import.meta.env.VITE_OAUTH_WORKER_URL || 'https://auth.xivdyetools.app';

/**
 * Upper bound on the best-effort `/auth/revoke` request sent on logout (BUG-115)
 */
const REVOKE_TIMEOUT_MS = 5000;

/**
 * A signal that aborts after `ms`. `AbortSignal.timeout` needs Safari 16 /
 * Chrome 103 / Firefox 100 and the build targets es2020; where it is missing,
 * calling it threw inside the revoke's try and NO revoke was sent at all, so
 * fall back to an AbortController aborted by a timer
 */
function timeoutSignal(ms: number): AbortSignal {
  if (typeof AbortSignal.timeout === 'function') {
    return AbortSignal.timeout(ms);
  }
  const controller = new AbortController();
  setTimeout(() => controller.abort(), ms);
  return controller.signal;
}

/**
 * Presets API URL - handles preset operations
 */
const PRESETS_API_URL = import.meta.env.VITE_PRESETS_API_URL || 'https://api.xivdyetools.app';

/**
 * Storage key for auth token
 *
 * SECURITY NOTE: Auth tokens are stored in localStorage for the following reasons:
 * - Enables cross-tab session sharing (improves UX)
 * - Simpler architecture without httpOnly cookie requirements on OAuth worker
 * - Token expiry is validated on each auth check
 *
 * Mitigations in place:
 * 1. Strict CSP prevents inline script execution (no unsafe-eval, no unsafe-inline for scripts)
 * 2. Token expiry validation on every isAuthenticated() call
 * 3. Server-side token revocation on logout
 * 4. All API endpoints validate token server-side
 *
 * Trade-off: If CSP were bypassed via XSS, tokens could be exfiltrated.
 * Future consideration: httpOnly cookies via OAuth worker for defense-in-depth.
 *
 * @see https://cheatsheetseries.owasp.org/cheatsheets/JSON_Web_Token_for_Java_Cheat_Sheet.html#token-storage-on-client-side
 */
const TOKEN_STORAGE_KEY = 'xivdyetools_auth_token';

/**
 * Storage key for token expiry
 */
const EXPIRY_STORAGE_KEY = 'xivdyetools_auth_expires';

/**
 * Session storage keys for OAuth flow
 */
const PKCE_VERIFIER_KEY = 'xivdyetools_pkce_verifier';
const OAUTH_STATE_KEY = 'xivdyetools_oauth_state';
const OAUTH_RETURN_PATH_KEY = 'xivdyetools_oauth_return_path';
const OAUTH_RETURN_TOOL_KEY = 'xivdyetools_oauth_return_tool';
const OAUTH_PROVIDER_KEY = 'xivdyetools_oauth_provider';

/**
 * Storage key for auth provider
 */
const PROVIDER_STORAGE_KEY = 'xivdyetools_auth_provider';

/**
 * Validate and sanitize return path to prevent open redirect attacks
 *
 * SECURITY: Only allows:
 * - Relative paths starting with /
 * - No protocol (://)
 * - No protocol-relative URLs (//)
 * - No external domains
 *
 * @returns Sanitized path or '/' if invalid
 */
function sanitizeReturnPath(path: string | null): string {
  if (!path) return '/';

  // Must be a string
  if (typeof path !== 'string') return '/';

  // Trim whitespace
  const trimmed = path.trim();

  // Must start with exactly one /
  if (!trimmed.startsWith('/')) return '/';

  // Block protocol-relative URLs (//evil.com)
  if (trimmed.startsWith('//')) return '/';

  // Block any URLs with protocol
  if (trimmed.includes('://')) return '/';

  // Block javascript: protocol (case insensitive)
  if (trimmed.toLowerCase().includes('javascript:')) return '/';

  // Block data: protocol
  if (trimmed.toLowerCase().includes('data:')) return '/';

  // Parse as URL to check for host bypass attempts
  try {
    const url = new URL(trimmed, window.location.origin);
    // Must be same origin
    if (url.origin !== window.location.origin) return '/';
    // Return just the path (strips any accidentally parsed host)
    return url.pathname + url.search + url.hash;
  } catch {
    // If parsing fails, use the trimmed path if it looks safe
    // Only allow simple paths without special characters at start
    if (/^\/[a-zA-Z0-9\-_/]*$/.test(trimmed)) {
      return trimmed;
    }
    return '/';
  }
}

// ============================================
// Auth Service
// ============================================

class AuthServiceImpl {
  private state: AuthState = {
    isAuthenticated: false,
    user: null,
    token: null,
    expiresAt: null,
    provider: null,
  };

  private listeners: Set<AuthStateListener> = new Set();
  private initialized = false;
  // BUG-025: `isAuthenticated()` fires `void this.logout()` from a
  // synchronous getter with real multi-listener call sites (preset-detail,
  // community-preset-service, preset-submission-service, config-sidebar).
  // Without memoising the in-flight call, N synchronous callers on an
  // expired token each started their own `/auth/revoke` request and their
  // own listener-notify pass. `logout()` now returns the same in-flight
  // promise to every caller until it settles.
  private logoutPromise: Promise<void> | null = null;

  /**
   * Initialize auth service - restore session from storage
   */
  async initialize(): Promise<void> {
    if (this.initialized) return;

    // BUG-002: Sync auth state across tabs via StorageEvent
    // StorageEvent fires on all tabs EXCEPT the one that made the change — the originating tab
    // already has its state updated. Because of the `if (this.initialized) return` guard above,
    // this listener is registered exactly once per page lifetime.
    window.addEventListener('storage', (event) => {
      if (event.key === TOKEN_STORAGE_KEY) {
        if (event.newValue === null && this.state.isAuthenticated) {
          // Token removed in another tab — log out this tab too
          logger.info('Token cleared in another tab, synchronizing logout');
          this.clearState();
          this.notifyListeners();
        } else if (event.newValue !== null && !this.state.isAuthenticated) {
          // Token set in another tab — sync login state into this tab
          this.loadFromStorage();
          this.notifyListeners();
        }
      }
    });

    if (import.meta.env.DEV) {
      logger.info('🔐 [AuthService] Initializing...', { url: window.location.href });
    }

    try {
      this.loadFromStorage();

      // Check if we're handling an OAuth callback
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const error = urlParams.get('error');
      // `provider=<name>` is the oauth worker's marker on the XIVAuth callback
      // redirect. SECURITY (FINDING-032 / WEB-3): honour it only on an actual
      // callback (a `code` is present) and only for a known provider — it
      // used to be persisted from ANY page load, so a crafted share link
      // (`/presets?provider=xivauth`) could route the victim's next Discord
      // code exchange to the XIVAuth endpoint and silently fail the sign-in.
      const providerFromUrl = this.parseProvider(urlParams.get('provider'));
      if (code && providerFromUrl) {
        sessionStorage.setItem(OAUTH_PROVIDER_KEY, providerFromUrl);
      }

      if (import.meta.env.DEV) {
        logger.info('🔐 [AuthService] URL params:', {
          hasCode: !!code,
          hasError: !!error,
          provider: providerFromUrl,
        });
      }

      if (code) {
        // New secure PKCE flow: we receive the auth code, then exchange it with our stored code_verifier
        if (import.meta.env.DEV) {
          logger.info('🔐 [AuthService] Auth code found in URL, exchanging for token...');
        }
        await this.handleCallbackCode(code, urlParams.get('csrf'), urlParams.get('state'));
        // Get return path before cleaning URL, default to home
        // SECURITY: Sanitize to prevent open redirect attacks
        const rawPath =
          urlParams.get('return_path') || sessionStorage.getItem(OAUTH_RETURN_PATH_KEY);
        const returnPath = sanitizeReturnPath(rawPath);
        if (import.meta.env.DEV) {
          logger.info(`🔐 [AuthService] Navigating to return path: ${returnPath}`);
        }
        sessionStorage.removeItem(OAUTH_RETURN_PATH_KEY);
        // Clean up URL and navigate to return path
        this.navigateAfterAuth(returnPath);
      } else if (error) {
        logger.error('OAuth error:', error);
        // Get return path before cleaning URL
        // SECURITY: Sanitize to prevent open redirect attacks
        const rawPath =
          urlParams.get('return_path') || sessionStorage.getItem(OAUTH_RETURN_PATH_KEY);
        const returnPath = sanitizeReturnPath(rawPath);
        sessionStorage.removeItem(OAUTH_RETURN_PATH_KEY);
        // Clean up URL and navigate back (even on error)
        this.navigateAfterAuth(returnPath);
      }

      this.initialized = true;
      if (import.meta.env.DEV) {
        logger.info(
          `✅ [AuthService] Initialized: ${this.state.isAuthenticated ? 'Logged in as ' + this.state.user?.username : 'Not logged in'}`
        );
      }
    } catch (err) {
      logger.error('Failed to initialize auth service:', err);
      this.initialized = true;
    }
  }

  /**
   * Load auth state from localStorage
   */
  private loadFromStorage(): void {
    try {
      const token = localStorage.getItem(TOKEN_STORAGE_KEY);
      const expiresAtStr = localStorage.getItem(EXPIRY_STORAGE_KEY);

      if (!token || !expiresAtStr) {
        logger.info('🔐 No stored auth found, clearing state');
        this.clearState();
        return;
      }

      const expiresAt = parseInt(expiresAtStr, 10);
      const now = Math.floor(Date.now() / 1000);

      // Check if token is expired. BUG-063: a corrupt or non-numeric stored
      // value parses to NaN, and `NaN < now` is FALSE -- so a damaged expiry
      // read as "not expired" and produced a client session that never ended.
      // An unparseable expiry is not a valid session; treat it as expired.
      if (!Number.isFinite(expiresAt) || expiresAt < now) {
        logger.info('Stored token has expired, clearing session');
        this.clearStorage();
        this.clearState();
        return;
      }

      // Decode token to get user info
      const payload = this.decodeJWT(token);
      if (!payload) {
        this.clearStorage();
        this.clearState();
        return;
      }

      // Get stored provider or infer from payload
      const storedProvider = localStorage.getItem(PROVIDER_STORAGE_KEY) as AuthProvider | null;
      const provider = storedProvider || payload.auth_provider || 'discord';

      // Build avatar URL based on provider
      let avatarUrl: string | null = null;
      if (provider === 'discord' && payload.discord_id && payload.avatar) {
        avatarUrl = this.getAvatarUrl(payload.discord_id, payload.avatar);
      } else if (provider === 'xivauth') {
        // XIVAuth avatar URL is stored directly (no CDN construction needed)
        // We'll get it from the auth response, for now use null
        avatarUrl = null;
      }

      this.state = {
        isAuthenticated: true,
        token,
        expiresAt,
        provider,
        user: {
          id: presetsIdentity(payload),
          username: payload.username,
          global_name: payload.global_name,
          avatar: payload.avatar,
          avatar_url: avatarUrl,
          auth_provider: payload.auth_provider,
        },
      };
    } catch (err) {
      logger.error('Error loading auth from storage:', err);
      this.clearStorage();
      this.clearState();
    }
  }

  /**
   * Narrow an externally supplied provider name (URL param, sessionStorage)
   * to the known set; anything else is `null` (→ the Discord default).
   */
  private parseProvider(value: string | null): AuthProvider | null {
    return value === 'discord' || value === 'xivauth' ? value : null;
  }

  /**
   * Handle authorization code received from OAuth callback
   * Exchanges the code for a token via POST to the OAuth worker
   * This is the secure PKCE flow - the code_verifier never leaves the client
   *
   * @param code - Authorization code from the bounce
   * @param csrf - The SPA's own state nonce echoed by the worker (CSRF check)
   * @param signedState - The worker's signed state envelope (`?state=`), forwarded
   *   so the worker can bind the verifier to its challenge; null on older bounces
   */
  private async handleCallbackCode(
    code: string,
    csrf: string | null,
    signedState: string | null
  ): Promise<void> {
    // Retrieve the stored code_verifier (stored during login initiation)
    const codeVerifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
    const storedState = sessionStorage.getItem(OAUTH_STATE_KEY);
    const provider = this.parseProvider(sessionStorage.getItem(OAUTH_PROVIDER_KEY)) ?? 'discord';

    // Clean up PKCE session storage
    sessionStorage.removeItem(PKCE_VERIFIER_KEY);
    sessionStorage.removeItem(OAUTH_STATE_KEY);
    sessionStorage.removeItem(OAUTH_PROVIDER_KEY);

    if (!codeVerifier) {
      logger.error('Missing code_verifier - OAuth flow may have been tampered with');
      return;
    }

    // Verify CSRF state matches (fail-closed: reject if either value is missing)
    if (!csrf || !storedState || csrf !== storedState) {
      logger.error('CSRF state validation failed');
      return;
    }

    try {
      // Determine endpoint based on provider
      const callbackEndpoint =
        provider === 'xivauth'
          ? `${OAUTH_WORKER_URL}/auth/xivauth/callback`
          : `${OAUTH_WORKER_URL}/auth/callback`;

      if (import.meta.env.DEV) {
        logger.info(`🔐 [AuthService] Exchanging code via ${provider} endpoint`);
      }

      // Exchange code for token via POST (code_verifier sent directly, not through redirect).
      // FINDING-012 / OAUTH-5: the worker's bounce echoes its signed `state`
      // envelope; forwarding it lets the worker bind this verifier to the
      // challenge it issued (S256(code_verifier) must equal state.code_challenge).
      // Older bounces carry none — then the body is exactly what it was.
      const response = await fetch(callbackEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code,
          code_verifier: codeVerifier,
          redirect_uri: `${window.location.origin}/auth/callback`,
          ...(signedState ? { state: signedState } : {}),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logger.error('Token exchange failed:', errorData);
        return;
      }

      const data: AuthResponse = await response.json();

      if (!data.success) {
        logger.error('Token exchange returned error:', data.error);
        return;
      }

      // Contract guarantees `token` once `success` is true; stay defensive
      // against an empty string without depending on `.error`, which only
      // exists on the failure branch of the union.
      if (!data.token) {
        logger.error('Token exchange succeeded but returned no token');
        return;
      }

      // Store provider for future reference
      localStorage.setItem(PROVIDER_STORAGE_KEY, provider);

      // Process the received token
      await this.handleCallbackToken(data.token, data.expires_at?.toString() || null);
    } catch (err) {
      logger.error('Error exchanging code for token:', err);
    }
  }

  /**
   * Handle token received from OAuth callback
   * Called after successful code exchange
   */
  private async handleCallbackToken(token: string, expiresAtStr: string | null): Promise<void> {
    const payload = this.decodeJWT(token);
    if (!payload) {
      logger.error('Invalid token - decode failed');
      return;
    }

    // BUG-063: `parseInt` of a malformed query parameter is NaN, which the
    // `expiresAt &&` guard below treats as "no value" and which would then be
    // written to storage as the string "NaN".
    const parsedExpiry = expiresAtStr ? parseInt(expiresAtStr, 10) : payload.exp;
    let expiresAt = Number.isFinite(parsedExpiry) ? parsedExpiry : payload.exp;
    // BUG-001: Guard against millisecond timestamps — JWT exp is always Unix seconds (RFC 7519)
    // A real seconds timestamp for year 2026 is ~1.77e9, well under 1e12
    if (expiresAt && expiresAt > 1e12) {
      logger.warn('Token expiry looks like milliseconds — converting to seconds');
      expiresAt = Math.floor(expiresAt / 1000);
    }
    const provider = payload.auth_provider || 'discord';

    // SECURITY: Store token in localStorage (see TOKEN_STORAGE_KEY doc for security rationale)
    // Using localStorage instead of sessionStorage enables cross-tab session sharing
    // Token is never logged; only expiry metadata is logged below
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
    localStorage.setItem(EXPIRY_STORAGE_KEY, expiresAt.toString());
    localStorage.setItem(PROVIDER_STORAGE_KEY, provider);
    logger.info(`Token stored, expires: ${new Date(expiresAt * 1000).toISOString()}`);

    // Build avatar URL based on provider
    let avatarUrl: string | null = null;
    if (provider === 'discord' && payload.discord_id && payload.avatar) {
      avatarUrl = this.getAvatarUrl(payload.discord_id, payload.avatar);
    }

    // Update state
    this.state = {
      isAuthenticated: true,
      token,
      expiresAt,
      provider,
      user: {
        id: presetsIdentity(payload),
        username: payload.username,
        global_name: payload.global_name,
        avatar: payload.avatar,
        avatar_url: avatarUrl,
        auth_provider: payload.auth_provider,
      },
    };

    this.notifyListeners();

    logger.info(`Logged in as ${this.state.user?.global_name || this.state.user?.username}`);

    // Refresh author name on all user's presets (fire-and-forget)
    // This keeps preset attribution in sync with the user's current Discord display name
    this.refreshAuthorName().catch((err) => {
      logger.warn('Failed to refresh author name on presets:', err);
    });
  }

  /**
   * Refresh the author name on all presets submitted by this user
   * Called on login to sync with the user's current Discord display name
   */
  private async refreshAuthorName(): Promise<void> {
    if (!this.state.token) return;

    try {
      const response = await fetch(`${PRESETS_API_URL}/api/v1/presets/refresh-author`, {
        method: 'PATCH',
        headers: this.getAuthHeaders(),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.updated > 0) {
          logger.info(`Updated author name on ${result.updated} preset(s)`);
        }
      }
    } catch (err) {
      // Non-blocking - don't throw, just log
      logger.warn('Error refreshing author name:', err);
    }
  }

  /**
   * Navigate to the return path after OAuth callback
   * This handles both successful login and error cases
   */
  private navigateAfterAuth(returnPath: string): void {
    // Check if a returnTool was stored during login initiation
    const returnTool = sessionStorage.getItem(OAUTH_RETURN_TOOL_KEY);
    sessionStorage.removeItem(OAUTH_RETURN_TOOL_KEY);

    // If returnTool is specified, navigate to that tool's route instead
    let finalPath = returnPath;
    if (returnTool) {
      finalPath = `/${returnTool}`;
      if (import.meta.env.DEV) {
        logger.info(`🔐 [AuthService] Using returnTool: ${returnTool} -> ${finalPath}`);
      }
    }

    // Use replaceState to avoid adding callback URL to history
    // Then navigate to the return path
    const targetUrl = new URL(window.location.origin + finalPath);
    window.history.replaceState({}, '', targetUrl.toString());
    // Force page reload to re-render with new auth state
    // This ensures all components see the updated auth state
    window.location.reload();
  }

  /**
   * Clear auth state
   */
  private clearState(): void {
    this.state = {
      isAuthenticated: false,
      user: null,
      token: null,
      expiresAt: null,
      provider: null,
    };
  }

  /**
   * Clear stored tokens
   */
  private clearStorage(): void {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(EXPIRY_STORAGE_KEY);
    localStorage.removeItem(PROVIDER_STORAGE_KEY);
  }

  // ============================================
  // Public API
  // ============================================

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    // Also check expiry
    // BUG-063: this was `if (this.state.expiresAt)`, and NaN is FALSY -- so a
    // corrupt expiry skipped the check entirely, the second half of the
    // never-expiring session. Test for "an expiry was set" explicitly, then
    // treat a non-finite one as expired.
    if (this.state.expiresAt !== null) {
      const now = Math.floor(Date.now() / 1000);
      if (!Number.isFinite(this.state.expiresAt) || this.state.expiresAt < now) {
        void this.logout();
        return false;
      }
    }
    return this.state.isAuthenticated;
  }

  /**
   * Get current auth state
   */
  getState(): Readonly<AuthState> {
    return { ...this.state };
  }

  /**
   * Get current user
   */
  getUser(): AuthUser | null {
    return this.state.user;
  }

  /**
   * Get auth headers for API requests
   */
  getAuthHeaders(): Record<string, string> {
    if (this.state.token && this.isAuthenticated()) {
      return { Authorization: `Bearer ${this.state.token}` };
    }
    return {};
  }

  /**
   * Initiate Discord OAuth login
   * @param returnPath - Path to return to after login
   * @param returnTool - Tool ID to return to after login (e.g., 'presets')
   *
   * SECURITY: The code_verifier is stored ONLY in sessionStorage and sent
   * directly to POST /auth/callback. It is NEVER sent through URL redirects.
   * This is the core security guarantee of PKCE.
   */
  async login(returnPath?: string, returnTool?: string): Promise<void> {
    logger.info('Initiating Discord OAuth login...');

    try {
      // Generate PKCE code verifier and challenge
      const codeVerifier = this.generateRandomString(64);
      const codeChallenge = await this.sha256Base64Url(codeVerifier);
      const state = this.generateRandomString(32);

      // Store for callback verification - code_verifier stays here, never sent via URL
      sessionStorage.setItem(PKCE_VERIFIER_KEY, codeVerifier);
      sessionStorage.setItem(OAUTH_STATE_KEY, state);
      sessionStorage.setItem(OAUTH_RETURN_PATH_KEY, returnPath || window.location.pathname);
      // Each flow starts clean: a provider marker left by an earlier XIVAuth
      // attempt (or a crafted link) must not route THIS code exchange to the
      // XIVAuth callback (FINDING-032 / WEB-3).
      sessionStorage.removeItem(OAUTH_PROVIDER_KEY);
      // Store return tool if provided
      if (returnTool) {
        sessionStorage.setItem(OAUTH_RETURN_TOOL_KEY, returnTool);
      }

      // Build auth URL - ONLY send code_challenge, NOT code_verifier
      const authUrl = new URL(`${OAUTH_WORKER_URL}/auth/discord`);
      authUrl.searchParams.set('code_challenge', codeChallenge);
      authUrl.searchParams.set('code_challenge_method', 'S256');
      authUrl.searchParams.set('state', state);
      authUrl.searchParams.set('redirect_uri', `${window.location.origin}/auth/callback`);
      if (returnPath) {
        authUrl.searchParams.set('return_path', returnPath);
      }

      // Redirect to OAuth
      window.location.href = authUrl.toString();
    } catch (err) {
      logger.error('Failed to initiate OAuth login:', err);
      throw err;
    }
  }

  /**
   * Initiate XIVAuth OAuth login
   * @param returnPath - Path to return to after login
   * @param returnTool - Tool ID to return to after login (e.g., 'presets')
   *
   * XIVAuth is an FFXIV community authentication provider that supports
   * character verification and can link to Discord accounts.
   */
  async loginWithXIVAuth(returnPath?: string, returnTool?: string): Promise<void> {
    logger.info('Initiating XIVAuth OAuth login...');

    try {
      // Generate PKCE code verifier and challenge
      const codeVerifier = this.generateRandomString(64);
      const codeChallenge = await this.sha256Base64Url(codeVerifier);
      const state = this.generateRandomString(32);

      // Store for callback verification
      sessionStorage.setItem(PKCE_VERIFIER_KEY, codeVerifier);
      sessionStorage.setItem(OAUTH_STATE_KEY, state);
      sessionStorage.setItem(OAUTH_RETURN_PATH_KEY, returnPath || window.location.pathname);
      sessionStorage.setItem(OAUTH_PROVIDER_KEY, 'xivauth'); // Mark as XIVAuth flow
      if (returnTool) {
        sessionStorage.setItem(OAUTH_RETURN_TOOL_KEY, returnTool);
      }

      // Build XIVAuth auth URL (via our worker)
      const authUrl = new URL(`${OAUTH_WORKER_URL}/auth/xivauth`);
      authUrl.searchParams.set('code_challenge', codeChallenge);
      authUrl.searchParams.set('code_challenge_method', 'S256');
      authUrl.searchParams.set('state', state);
      authUrl.searchParams.set('redirect_uri', `${window.location.origin}/auth/callback`);
      if (returnPath) {
        authUrl.searchParams.set('return_path', returnPath);
      }

      // Redirect to XIVAuth OAuth
      window.location.href = authUrl.toString();
    } catch (err) {
      logger.error('Failed to initiate XIVAuth login:', err);
      throw err;
    }
  }

  /**
   * Logout - clear tokens and notify listeners
   *
   * BUG-025: memoises the in-flight logout so concurrent/re-entrant callers
   * (e.g. several synchronous `isAuthenticated()` checks on an expired
   * token) share one revoke request and one notify pass instead of each
   * starting their own.
   */
  async logout(): Promise<void> {
    if (this.logoutPromise) {
      return this.logoutPromise;
    }

    // Deferred one microtask: performLogout() notifies listeners synchronously,
    // and the memo must already be in place by then so a re-entrant logout()
    // shares this pass instead of starting another
    const promise = Promise.resolve()
      .then(() => this.performLogout())
      .finally(() => {
        this.logoutPromise = null;
      });
    this.logoutPromise = promise;
    return promise;
  }

  private performLogout(): void {
    logger.info('Logging out...');

    // BUG-115: the local session is cleared FIRST. The revoke used to be
    // awaited before any of this, with no timeout, so an auth worker that
    // accepted the connection and never answered left the token stored and
    // the UI signed in. Read the token for the revoke before clearing it.
    const token = this.state.token;

    this.clearStorage();
    this.clearState();
    // A half-finished OAuth flow must not outlive the session it started in
    sessionStorage.removeItem(OAUTH_PROVIDER_KEY);
    // FINDING-008: Clear cached market prices so they don't persist across sessions
    void APIService.clearCache().catch(() => {});
    this.notifyListeners();

    // Revoke on the server: best effort, never awaited, and bounded so a
    // stalled connection cannot linger
    if (token) {
      try {
        void fetch(`${OAUTH_WORKER_URL}/auth/revoke`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal: timeoutSignal(REVOKE_TIMEOUT_MS),
        }).catch(() => {
          // Ignore revoke errors, the timeout included
        });
      } catch {
        // Ignore revoke errors
      }
    }

    logger.info('Logged out successfully');
  }

  /**
   * Subscribe to auth state changes
   */
  subscribe(listener: AuthStateListener): () => void {
    this.listeners.add(listener);
    // Immediately call with current state
    listener(this.getState());

    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notify all listeners of state change
   */
  private notifyListeners(): void {
    const state = this.getState();
    this.listeners.forEach((listener) => {
      try {
        listener(state);
      } catch (err) {
        logger.error('Auth listener error:', err);
      }
    });
  }

  // ============================================
  // Utility Methods
  // ============================================

  /**
   * Decode JWT without verification (for reading payload)
   */
  private decodeJWT(token: string): JWTPayload | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;

      // Decode payload (second part)
      const payload = parts[1];
      let base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const padding = base64.length % 4;
      if (padding) {
        base64 += '='.repeat(4 - padding);
      }

      // BUG-060: atob() returns a BINARY string -- one character per byte. A
      // JWT payload is UTF-8, so every non-ASCII character (a Japanese, Korean
      // or accented Discord display name) arrives as mojibake unless the bytes
      // are decoded as UTF-8 first. `JSON.parse` does not do that for us.
      const binary = atob(base64);
      const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
      return JSON.parse(new TextDecoder().decode(bytes));
    } catch {
      return null;
    }
  }

  /**
   * Get Discord avatar URL
   */
  private getAvatarUrl(userId: string, avatarHash: string | null): string | null {
    if (!avatarHash) return null;
    const format = avatarHash.startsWith('a_') ? 'gif' : 'png';
    return `https://cdn.discordapp.com/avatars/${userId}/${avatarHash}.${format}`;
  }

  /**
   * Generate cryptographically random string
   */
  private generateRandomString(length: number): string {
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);
    return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
  }

  /**
   * SHA-256 hash and base64url encode
   */
  private async sha256Base64Url(input: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(input);
    const hash = await crypto.subtle.digest('SHA-256', data);
    const bytes = new Uint8Array(hash);
    const base64 = btoa(String.fromCharCode(...bytes));
    // Convert to base64url
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
}

// ============================================
// Export Singleton
// ============================================

export const authService = new AuthServiceImpl();
